import { create } from 'zustand';

// ─── Typen ──────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  email: string;
  role: 'admin' | 'viewer';
  mustChangePassword: boolean;
  totpEnabled: boolean;
  webauthnEnabled: boolean;
  permissions: string[];
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  csrfToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;

  setAuth: (accessToken: string, user: AuthUser, csrfToken?: string) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  hasPermission: (permission: string) => boolean;
  /** Wie hasPermission, aber OHNE Admin-Bypass – für sensible Rechte (Medizin, Art. 9 DSGVO). */
  hasSensitivePermission: (permission: string) => boolean;
}

// ─── Store ──────────────────────────────────────────────────────────────────

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  csrfToken: null,
  isLoading: true, // Initial: Prüfe ob Session existiert
  isAuthenticated: false,
  isAdmin: false,

  // csrfToken bewahren, wenn der Aufrufer keinen liefert (z.B. Passkey-
  // Registrierung gibt nur accessToken+user zurück) – sonst würde ein gültiges
  // Token genullt und nachfolgende Mutationen scheiterten mit CSRF-Fehler.
  setAuth: (accessToken, user, csrfToken) =>
    set((state) => ({
      accessToken,
      csrfToken: csrfToken ?? state.csrfToken,
      user: { ...user, permissions: user.permissions ?? [] },
      isAuthenticated: true,
      isAdmin: user.role === 'admin',
      isLoading: false,
    })),

  clearAuth: () =>
    set({
      accessToken: null,
      csrfToken: null,
      user: null,
      isAuthenticated: false,
      isAdmin: false,
      isLoading: false,
    }),

  setLoading: (isLoading) => set({ isLoading }),

  hasPermission: (permission) => {
    const user = get().user;
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.permissions.includes(permission);
  },

  // Sensible Gesundheitsrechte: seit 2026-07-15 MIT Admin-Bypass (das Backend
  // spiegelt das in requireMedicalAccess). Nicht-Admins brauchen weiterhin
  // explizit zugewiesene medical.*-/medicalSensitive.*-Rechte.
  hasSensitivePermission: (permission) => {
    const user = get().user;
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.permissions.includes(permission);
  },
}));
